import React, { useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { ProjectActions, ProjectRow, ProjectsHead, StyledH3, StyledLabel, StyledProjectName } from 'components';
import ProjectDetails from 'components/Projects/ProjectDetails';
import { AsyncImage } from 'components/AsyncImage';
import { DehydratedProject } from 'au-js-sdk/lib/models/Project';
import { useProductSelectors } from 'hooks/useProductSelectors';
import useProjects from 'hooks/useProjects';
import { getCustomAttribute } from 'helpers/mage-helpers/magento';
import { customerCustomAttributesSelector } from 'store/customer/selectors';
import { HeaderWrapper, ThumbnailColumn, StyledProjectOption } from './styled';
import { DeviceSize, useWindowSize } from 'hooks/useWindowSize';
import { transformToEllipsisText } from 'helpers/strings';

export const RecentProjects = (): JSX.Element => {
  const customAttributes = useSelector(customerCustomAttributesSelector, shallowEqual);
  const flashToken = getCustomAttribute('flash_auth_token')(customAttributes);
  const flashId = getCustomAttribute('flash_id')(customAttributes);
  const { findProductBySku } = useProductSelectors();

  const {
    projects,
    deleteProjectHandler,
    duplicateProjectHandler,
    addProjectHandler,
    editProjectHandler,
    getProjectThumbnailUrl,
    isEditable
  } = useProjects(flashId, flashToken, 2, true);

  const addProject = useCallback(
    (project: DehydratedProject) => {
      const product = findProductBySku(project.product.sku)?.node;
      if (product) {
        return addProjectHandler(project, product);
      }
    },
    [addProjectHandler, findProductBySku]
  );

  const { deviceSize } = useWindowSize();
  const isMobile = deviceSize === DeviceSize.SMALL;

  const formatProjectName = (projectName: string) => {
    if (isMobile) {
      return transformToEllipsisText(projectName, 38, isMobile);
    }
    if (!isMobile) {
      return transformToEllipsisText(projectName, 28, isMobile);
    }
    return projectName;
  };

  return (
    <>
      <HeaderWrapper>
        <StyledH3 secondary>Recent Projects</StyledH3>
      </HeaderWrapper>
      <ProjectsHead columnWidth="14rem" alignItems="center">
        <StyledLabel>Project</StyledLabel>
        <StyledLabel>Project Name</StyledLabel>
        <StyledLabel>Details</StyledLabel>
        <StyledLabel></StyledLabel>
      </ProjectsHead>
      {projects.map(project => {
        const { sku, options } = project.product;
        const projectProductExists = findProductBySku(sku)?.node != null;
        return (
          <ProjectRow key={project.id} columnWidth="14rem">
            <ThumbnailColumn>
              <AsyncImage
                sourceLoader={() => getProjectThumbnailUrl(sku, options, project.schemaVersion)}
                alt={`Project ${project.name}`}
              />
              {isEditable(project) && (
                <StyledProjectOption onClick={editProjectHandler(project)}>Edit</StyledProjectOption>
              )}
            </ThumbnailColumn>
            <StyledProjectName secondary title={project.name}>
              {formatProjectName(project.name)}
            </StyledProjectName>
            <ProjectDetails project={project} />
            <ProjectActions rowHeight="2rem">
              {isEditable(project) ? (
                <StyledProjectOption onClick={editProjectHandler(project)}>Review &amp; Order</StyledProjectOption>
              ) : projectProductExists ? (
                <StyledProjectOption onClick={addProject(project)}>Add to Cart</StyledProjectOption>
              ) : null}
              <StyledProjectOption onClick={() => duplicateProjectHandler(project)}>Duplicate</StyledProjectOption>
              <StyledProjectOption onClick={deleteProjectHandler(project)}>Delete</StyledProjectOption>
            </ProjectActions>
          </ProjectRow>
        );
      })}
    </>
  );
};
